import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { CornerType, ISunSailData } from "@biz/definitions/SunSailDefinitions";
import { getArticleForCornerType, getEdgeName } from "@biz/definitions/SunSailDefinitions.util";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { calcSailPlateInfo, calcSailPlateMatrixAxle } from "@biz/items/sunSail/SunSailClothLogic";
import { ConcavityHelper } from "@biz/logic/ConcavityHelper";
import { isPointInPolygon } from "@biz/logic/PointInPoly";

interface ISvgPolyPointInfo {
  pos: IVec3;
  concavity: number;
  cornerType?: CornerType;
}

export class Sail2dPoly {
  static isPointInPoly(poly: IVec2[], obstalcePos: IVec2): boolean {
    return isPointInPolygon(poly, obstalcePos);
  }

  static isPointInPolys(polys: IVec2[][], obstalcePos: IVec2): boolean {
    for (const poly of polys) {
      if (Sail2dPoly.isPointInPoly(poly, obstalcePos))
        return true;
    }
    return false;
  }

  static calcSvgPolyPoints(ssd: ISunSailData, groupIndex: number): IVec2[] {
    const pointInfos = Sail2dPoly.calcSunSailPolyInfo(ssd, groupIndex)
    return Sail2dPoly.calcSvgPolyPointsFromPolyInfos(pointInfos, groupIndex);
  }

  static calcSvgPolyPointsFromPolyInfos(pointInfos: ISvgPolyPointInfo[], groupIndex: number): IVec2[] {
    const polyPoints: IVec2[] = [];
    for (let iPoint = 0; iPoint < pointInfos.length; iPoint++) {
      const currentPositions = Sail2dPoly.getPointPositions(pointInfos, iPoint);
      if (currentPositions.plate) {
        polyPoints.push(Vec2.fromXZ(currentPositions.plate[0]))
        // pointString += currentPositions[0].x + " " + currentPositions[0].z + " ";
      }

      const currentPoint = pointInfos[iPoint];
      const nextPositions = Sail2dPoly.getPointPositions(pointInfos, (iPoint + 1) % pointInfos.length);

      const helper = new ConcavityHelper(currentPositions.plate ? currentPositions.plate[1] : currentPositions.original, nextPositions.plate ? nextPositions.plate[0] : nextPositions.original, currentPoint.concavity, groupIndex === 0 ? 1 : -1, undefined);
      polyPoints.push(...helper.calcStepArray().map(Vec2.fromXZ))
    }
    return polyPoints;
  }

  static calcSvgPolys(ssd: ISunSailData): IVec2[][] {
    const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));
    return topology.groups.map((_g, index) => Sail2dPoly.calcSvgPolyPoints(ssd, index))
  }

  static getPointPositions(points: ISvgPolyPointInfo[], index: number): { original: IVec3, plate?: IVec3[] } {
    const current = points[index];
    if (!current.cornerType || current.cornerType === "None")
      return { original: current.pos };

    const matrix = calcSailPlateMatrixAxle(current.pos, points[(index + points.length - 1) % points.length].pos, points[(index + 1) % points.length].pos)
    const cornerArticle = getArticleForCornerType(current.cornerType);
    const sailPlateInfo = calcSailPlateInfo(cornerArticle);

    return {
      original: current.pos,
      plate: [
        Vec3.applyMatrix4(sailPlateInfo.offsetRight, matrix),
        Vec3.applyMatrix4(sailPlateInfo.offsetLeft, matrix)
      ]
    }
  }

  static calcSunSailPolyInfo(ssd: ISunSailData, groupIndex: number): ISvgPolyPointInfo[] {
    const topology = SunSailTopology.calcTopology(ssd.sunSailType, Poles.getCount(ssd));

    const netGroupPositions = SunSailLogic.calcNetPositionGroups(ssd, {});

    const g = topology.groups[groupIndex];
    const poly = g.poleIndices.map<ISvgPolyPointInfo>((pi, index) => {
      const next = g.poleIndices[(index + 1) % g.poleIndices.length]
      const en = getEdgeName(pi, next);
      return {
        pos: netGroupPositions[groupIndex][pi],
        concavity: SunSailLogic.getConcavityPercent(ssd, en),
        cornerType: SunSailLogic.getCornerType(ssd, pi)
      }
    })

    return poly;
  }
}